import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import './ep.css';
import { epApi, showError, searchParams } from './api';
import FiltersPanel, { DEFAULT_FILTERS } from './components/FiltersPanel';
import ProgramCardView from './components/ProgramCardView';
import SuggestedForYou from './components/SuggestedForYou';
import EpNav from './components/EpNav';
import SuggestProgram from './components/SuggestProgram';
import { useEpUser, useLoad, useLocalState } from './hooks';
import { Eligibility, Filters, ProgramCard, TopicGroup } from './types';

const PAGE = 24;

/**
 * Discover: search every Educational Program with the filters families asked for.
 * Shared by the student (Save) and, through props, the counselor and parent tabs.
 */
export default function DiscoverPage({
  forStudentName,
  cardAction,
  storageKey = 'ep.discover.filters',
  initialFilters,
  loadEligibility,
  openHref,
  embedded,
}: {
  forStudentName?: string;
  cardAction?: (p: ProgramCard) => React.ReactNode;
  storageKey?: string;
  initialFilters?: Partial<Filters>;
  /** Counselor and parent tab: the linked student's eligibility instead of the signed-in person's. */
  loadEligibility?: () => Promise<Eligibility | null>;
  /** Where a card opens. Defaults to the program page. */
  openHref?: (programId: number) => string;
  /** Inside another page (no page padding or title). */
  embedded?: boolean;
} = {}) {
  const history = useHistory();
  const { isStudent, userId } = useEpUser();
  const [filters, setFilters] = useLocalState<Filters>(storageKey, { ...DEFAULT_FILTERS, ...initialFilters });
  const [q, setQ] = useState(filters.q);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ProgramCard[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [zipUnknown, setZipUnknown] = useState(false);
  const [saved, setSaved] = useState<Set<number>>(new Set());

  const topics = useLoad<TopicGroup[]>(() => epApi.topics(), []);
  const elig = useLoad<Eligibility | null>(() => (loadEligibility ? loadEligibility() : isStudent ? epApi.myEligibility() : Promise.resolve(null)), [isStudent]);

  useEffect(() => {
    if (isStudent) epApi.savedIds().then(setSaved).catch(() => undefined);
  }, [isStudent]);

  // Typing waits a moment before searching.
  useEffect(() => {
    const t = setTimeout(() => {
      if (q !== filters.q) {
        setFilters({ ...filters, q, sort: q && filters.sort === 'deadline' ? 'best' : !q && filters.sort === 'best' ? 'deadline' : filters.sort });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const paramsKey = JSON.stringify(searchParams(filters, elig.data ?? null, 1, PAGE));
  const lastKey = useRef(paramsKey);
  useEffect(() => {
    // New filters start again at page 1.
    if (lastKey.current !== paramsKey) {
      lastKey.current = paramsKey;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }
    let live = true;
    setLoading(true);
    epApi
      .search(searchParams(filters, elig.data ?? null, page, PAGE))
      .then((r) => {
        if (!live) return;
        setCount(r.count);
        setZipUnknown(!!r.near_zip_unknown);
        setItems((prev) => (page === 1 ? r.data : [...prev, ...r.data]));
      })
      .catch((e) => live && showError(e, 'Search did not load. Please try again.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, page]);

  const toggleSave = async (p: ProgramCard) => {
    const was = saved.has(p.id);
    const next = new Set(saved);
    if (was) next.delete(p.id);
    else next.add(p.id);
    setSaved(next);
    try {
      if (was) await epApi.unsave(p.id);
      else {
        await epApi.save(p.id);
        toast.success('Saved. Add it to Tracking from the program page to get a checklist.');
      }
    } catch (e) {
      setSaved(saved);
      showError(e, 'Could not save. Please try again.');
    }
  };

  const sorts = useMemo(() => {
    const s: [string, string][] = [];
    if (filters.q) s.push(['best', 'Best match']);
    s.push(['deadline', 'Deadline soonest'], ['cost_low', 'Price, low to high'], ['cost_high', 'Price, high to low'], ['name', 'Name']);
    if (/^\d{5}$/.test(filters.near_zip)) s.push(['distance', 'Closest first']);
    return s;
  }, [filters.q, filters.near_zip]);

  return (
    <div className={embedded ? 'ep ep-embedded' : 'ep'}>
      {!embedded && (
      <div className="between" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>Educational Programs</h1>
          <p className="body" style={{ marginTop: 4 }}>
            Summer and year-round programs{forStudentName ? ` for ${forStudentName}` : ''}. Open one to see what it asks for and when.
          </p>
        </div>
        {isStudent && <EpNav active="find" studentId={userId} />}
      </div>
      )}
      <div className="disc">
        <FiltersPanel value={filters} onChange={setFilters} topics={topics.data ?? []} eligibility={elig.data ?? null} forName={forStudentName} profileZip={elig.data?.zip ?? null} />
        <section aria-label="Results">
          {isStudent && !embedded && userId > 0 && !filters.q && (
            <SuggestedForYou
              studentId={userId}
              saved={saved}
              onSave={(id) => {
                const p = items.find((x) => x.id === id) ?? ({ id } as ProgramCard);
                toggleSave(p);
              }}
            />
          )}
          <div className="row searchrow" style={{ flexWrap: 'nowrap' }}>
            <input
              className="in"
              type="search"
              placeholder="Search programs, schools or topics"
              aria-label="Search programs"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select className="in" style={{ width: 200 }} aria-label="Sort" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}>
              {sorts.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div className="results-head">
            <h2 aria-live="polite">{loading && page === 1 ? 'Searching' : `${count.toLocaleString('en-US')} ${count === 1 ? 'program' : 'programs'}`}</h2>
            <label className="check">
              <input type="checkbox" checked={filters.group_family} onChange={(e) => setFilters({ ...filters, group_family: e.target.checked })} />
              One card per school
            </label>
          </div>
          {zipUnknown && <p className="panel" style={{ marginBottom: 12 }}>We could not find that zip code, so distance is not used.</p>}
          {!loading && items.length === 0 && (
            <div className="card empty">
              <h3>No programs match all of these filters</h3>
              <p style={{ marginTop: 6 }}>Try removing a filter or searching a broader word.</p>
              <button type="button" className="btn" style={{ marginTop: 14 }} onClick={() => { setQ(''); setFilters({ ...DEFAULT_FILTERS }); }}>
                Clear search and filters
              </button>
            </div>
          )}
          <div>
            {items.map((p) => (
              <ProgramCardView
                key={p.id}
                p={p}
                saved={saved.has(p.id)}
                onSave={isStudent ? () => toggleSave(p) : undefined}
                onOpen={() => history.push(openHref ? openHref(p.id) : `/summer-programs/programs?id=${p.id}`)}
                onMoreFromFamily={(name) => {
                  setQ(name);
                  setFilters({ ...filters, q: name, group_family: false, sort: 'best' });
                }}
                action={cardAction?.(p)}
              />
            ))}
          </div>
          {items.length < count && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="btn" disabled={loading} onClick={() => setPage(page + 1)}>
                {loading ? 'Loading' : 'Show more programs'}
              </button>
            </div>
          )}
          {!loading && <SuggestProgram openHref={openHref} />}
        </section>
      </div>
    </div>
  );
}
